import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useReportFilters } from '~/context/ReportFiltersProvider';
import { FaEye, FaSort, FaSortUp, FaSortDown, FaSync, FaTimes, FaDownload, FaCog } from 'react-icons/fa';
import Breadcrumb from '~/components/Breadcrumbs/Breadcrumb';
import { Pagination } from '~/components/Pagination';
import { fetchCampaignApplications, fetchPatientServicesCoordinators } from '~/services/campaign';
import { formatDateIfValid } from '~/utils';
import { ALL_AVAILABLE_COLUMNS, DEFAULT_VISIBLE_COLUMNS, parseVisibleColumns } from './campaignApplicationColumns';

interface SearchResult {
    id: number;
    campaign: number;
    campaign_name: string;
    campaign_ntaf_id: string;
    campaign_status: {
        code: string;
        name: string;
    } | null;
    campaign_is_for: {
        id: number;
        code: string;
        name: string;
    } | null;
    patient_name: string;
    pc_first_name: string;
    pc_last_name: string;
    pc_phone_cell: string;
    pc_email: string;
    sections_completed: {
        completed: number;
        total: number;
    };
    campaign_csc: string;
    campaign_medical_reason: string;
    campaign_medical_issue: string;
    campaign_approved_date: string | null;
    ins_comments: string;
    ag_understand_hhl_may_use_photos: boolean;
    campaign_photo_url: string;
    update_date: string;
    created_date: string;
}

const FILTER_LABELS: Record<string, string> = {
    contact_name: 'Contact Name',
    client_name: 'Client Name',
    requirements_complete: 'Requirements Complete',
    community: 'Community',
    insurance: 'Insurance',
    csc: 'CSC',
    assignee: 'CSC',
    pc_relationship: 'Contact Relationship',
    referral_source: 'Referral Source',
    referred_by: 'Referred By',
    created_by: 'Created By',
    prescreen_result: 'Prescreen Result',
    photo_consented: 'Photo Consented',
};


const CampaignApplicationSearchResults: React.FC = () => {
    const [searchParams, setSearchParams] = useSearchParams();
    const navigate = useNavigate();
    const [results, setResults] = useState<SearchResult[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [totalCount, setTotalCount] = useState(0);
    const [sortField, setSortField] = useState<string>('');
    const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
    const [refreshKey, setRefreshKey] = useState(0);
    // Map of CSC Person id -> full name, used to label the CSC filter chip with a
    // name instead of the raw id sent by the `csc`/`assignee` filter param.
    const [cscNamesByPersonId, setCscNamesByPersonId] = useState<Record<string, string>>({});

    useEffect(() => {
        let cancelled = false;
        fetchPatientServicesCoordinators()
            .then((pscs: any[]) => {
                if (cancelled || !Array.isArray(pscs)) return;
                const map: Record<string, string> = {};
                pscs.forEach((psc) => {
                    if (psc?.person_id != null && psc?.full_name) {
                        map[String(psc.person_id)] = psc.full_name;
                    }
                });
                setCscNamesByPersonId(map);
            })
            .catch((err) => console.error('Failed to load CSC names:', err));
        return () => {
            cancelled = true;
        };
    }, []);

    const [visibleColumnKeys, setVisibleColumnKeys] = useState<string[]>(
        () => parseVisibleColumns(searchParams.get('visible_columns')),
    );
    const [showColumnSelector, setShowColumnSelector] = useState(false);
    const columnSelectorRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (columnSelectorRef.current && !columnSelectorRef.current.contains(event.target as Node)) {
                setShowColumnSelector(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const columns = useMemo(() => {
        return ALL_AVAILABLE_COLUMNS.filter(col => visibleColumnKeys.includes(col.key));
    }, [visibleColumnKeys]);

    const handleColumnToggle = (columnKey: string) => {
        setVisibleColumnKeys(prev => {
            if (prev.includes(columnKey)) {
                if (prev.length === 1) return prev;
                return prev.filter(k => k !== columnKey);
            }
            return [...prev, columnKey];
        });
    };

    const handleResetColumns = () => {
        setVisibleColumnKeys(DEFAULT_VISIBLE_COLUMNS);
    };

    const handleRefresh = useCallback(() => {
        setRefreshKey(prev => prev + 1);
    }, []);

    const renderCellContent = (app: SearchResult, columnKey: string) => {
        switch (columnKey) {
            case 'pc_name':
                return `${app.pc_first_name || ''} ${app.pc_last_name || ''}`.trim();
            case 'patient_name':
                return app.patient_name || '';
            case 'requirements': {
                const s = app.sections_completed || { completed: 0, total: 6 };
                return `${s.completed} of ${s.total}`;
            }
            case 'campaign_csc':
                return app.campaign_csc || '';
            case 'campaign_medical_reason':
                return app.campaign_medical_reason || '';
            case 'campaign_status':
                return app.campaign_status?.name || '';
            case 'pc_email':
                return app.pc_email || '';
            case 'pc_phone':
                return app.pc_phone_cell || '';
            case 'campaign_approved_date':
                return app.campaign_approved_date
                    ? formatDateIfValid(app.campaign_approved_date, 'MM/dd/yy')
                    : '';
            case 'created_date':
                return app.created_date
                    ? formatDateIfValid(app.created_date, 'MM/dd/yy')
                    : '';
            case 'update_date':
                return app.update_date
                    ? formatDateIfValid(app.update_date, 'MM/dd/yy')
                    : '';
            case 'photo_consented':
                return app.ag_understand_hhl_may_use_photos ? 'Yes' : 'No';
            case 'photo_url':
                // Return the raw URL so CSV export gets the link; the table cell
                // renders it as a clickable link (see table body below).
                return app.campaign_photo_url || '';
            default:
                return '';
        }
    };

    useEffect(() => {
        const performSearch = async () => {
            setLoading(true);
            setError(null);

            try {
                const page = parseInt(searchParams.get('page') || '1', 10);
                setCurrentPage(page);

                const sortBy = searchParams.get('sortBy');
                const sort = searchParams.get('sort');
                if (sortBy) {
                    setSortField(sortBy);
                    setSortDirection(sort === 'asc' ? 'asc' : 'desc');
                }

                const excludeKeys = ['page', 'sortBy', 'sort', 'visible_columns'];

                const params: Record<string, string> = {};
                const filters: Record<string, string> = {};

                if (sortBy) params.sortBy = sortBy;
                if (sort) params.sort = sort;

                searchParams.forEach((value, key) => {
                    if (excludeKeys.includes(key)) return;

                    if (key === 'contact_name' || key === 'client_name') {
                        filters.name = value;
                    } else if (key === 'csc') {
                        filters.assignee = value;
                    } else {
                        filters[key] = value;
                    }
                });

                const response = await fetchCampaignApplications(params, filters, {}, page);
                setResults(response.results || []);

                if (response.count !== undefined) {
                    setTotalCount(response.count);
                    setTotalPages(Math.ceil(response.count / 20));
                } else {
                    const list = Array.isArray(response.results) ? response.results : [];
                    setTotalCount(list.length);
                    setTotalPages(1);
                }
            } catch (err) {
                setError('Failed to perform search. Please try again.');
                console.error('Campaign application search error:', err);
            } finally {
                setLoading(false);
            }
        };

        performSearch();
    }, [searchParams, refreshKey]);

    const handleBackToSearch = () => {
        const params = new URLSearchParams(searchParams);
        params.delete('page');
        params.delete('sortBy');
        params.delete('sort');
        navigate(`/new-report?${params.toString()}`);
    };

    const handlePageChange = (newPage: number) => {
        const params = new URLSearchParams(searchParams);
        params.set('page', newPage.toString());
        setSearchParams(params);
    };

    const handleSort = (field: string) => {
        const params = new URLSearchParams(searchParams);

        if (sortField === field) {
            const newDirection = sortDirection === 'asc' ? 'desc' : 'asc';
            setSortDirection(newDirection);
            params.set('sortBy', field);
            params.set('sort', newDirection);
        } else {
            setSortField(field);
            setSortDirection('asc');
            params.set('sortBy', field);
            params.set('sort', 'asc');
        }

        params.set('page', '1');
        setSearchParams(params);
    };

    const getSortIcon = (field: string) => {
        if (sortField !== field) {
            return <FaSort className="ml-1 text-gray-400" />;
        }
        return sortDirection === 'asc' ?
            <FaSortUp className="ml-1 text-primary" /> :
            <FaSortDown className="ml-1 text-primary" />;
    };

    const activeFilters = useMemo(() => {
        const filters: { key: string; label: string; value: string; rawValue: string }[] = [];
        const excludeKeys = ['page', 'sortBy', 'sort', 'visible_columns'];

        searchParams.forEach((value, key) => {
            if (!excludeKeys.includes(key) && value) {
                const displayValue =
                    (key === 'csc' || key === 'assignee')
                        ? cscNamesByPersonId[value] || value
                        : value;
                filters.push({
                    key,
                    label: FILTER_LABELS[key] || key,
                    value: displayValue,
                    rawValue: value,
                });
            }
        });

        return filters;
    }, [searchParams, cscNamesByPersonId]);

    const handleRemoveFilter = (key: string, value?: string) => {
        const params = new URLSearchParams(searchParams);

        const allValues = params.getAll(key);
        if (allValues.length > 1 && value) {
            params.delete(key);
            allValues.filter(v => v !== value).forEach(v => params.append(key, v));
        } else {
            params.delete(key);
        }

        params.set('page', '1');
        setSearchParams(params);
    };

    const { clearFilterParams } = useReportFilters();
    const handleClearAllFilters = () => {
        clearFilterParams();
        navigate('/new-report');
    };

    const [isDownloading, setIsDownloading] = useState(false);

    const handleDownloadCSV = useCallback(async () => {
        if (totalCount === 0) return;

        setIsDownloading(true);

        try {
            const excludeKeys = ['page', 'sortBy', 'sort', 'visible_columns'];
            const filters: Record<string, string> = {};

            searchParams.forEach((value, key) => {
                if (!excludeKeys.includes(key)) {
                    if (key === 'contact_name' || key === 'client_name') {
                        filters.name = value;
                    } else if (key === 'csc') {
                        filters.assignee = value;
                    } else {
                        filters[key] = value;
                    }
                }
            });

            const allResults: SearchResult[] = [];
            const pageSize = 20;
            const totalPagesToFetch = Math.ceil(totalCount / pageSize);

            for (let page = 1; page <= totalPagesToFetch; page++) {
                const response = await fetchCampaignApplications({}, filters, {}, page);
                const pageResults = response.results || [];
                if (Array.isArray(pageResults)) {
                    allResults.push(...pageResults);
                }
            }

            if (allResults.length === 0) return;

            // Use only the user-selected visible columns for CSV export
            const csvColumns = columns.map(col => ({
                key: col.key,
                label: col.label,
            }));

            const escapeCSV = (value: string | null | undefined): string => {
                if (value === null || value === undefined) return '';
                const str = String(value);
                if (str.includes(',') || str.includes('"') || str.includes('\n')) {
                    return `"${str.replace(/"/g, '""')}"`;
                }
                return str;
            };

            const header = csvColumns.map(col => escapeCSV(col.label)).join(',');

            const rows = allResults.map((app: SearchResult) => {
                return csvColumns.map(col => {
                    return escapeCSV(renderCellContent(app, col.key));
                }).join(',');
            });

            const csvContent = [header, ...rows].join('\n');
            const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
            const url = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.setAttribute('href', url);
            link.setAttribute('download', `campaign-applications-${new Date().toISOString().split('T')[0]}.csv`);
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(url);
        } catch (err) {
            console.error('Error downloading CSV:', err);
        } finally {
            setIsDownloading(false);
        }
    }, [totalCount, searchParams, columns]);

    if (loading) {
        return (
            <>
                <Breadcrumb pageName='Campaign Application Report' parentPage='Reports' parentPath='/new-report' />
                <div className='flex justify-center items-center h-64'>
                    <div className='text-lg'>Loading search results...</div>
                </div>
            </>
        );
    }

    if (error) {
        return (
            <>
                <Breadcrumb pageName='Campaign Application Report' parentPage='Reports' parentPath='/new-report' />
                <div className='rounded-sm border border-stroke bg-white px-5 pt-6 pb-2.5 shadow-default sm:px-7.5 xl:pb-1'>
                    <div className='text-center py-8'>
                        <div className='text-red-500 text-lg mb-4'>{error}</div>
                        <button
                            onClick={handleBackToSearch}
                            className='rounded-lg bg-primary px-6 py-2 text-white hover:bg-primary/90 font-medium'
                        >
                            Back to Search
                        </button>
                    </div>
                </div>
            </>
        );
    }

    return (
        <>
            <Breadcrumb pageName='Campaign Application Report' parentPage='Reports' parentPath='/new-report' />

            <div className='rounded-sm border border-stroke bg-white px-5 pt-6 pb-2.5 shadow-default sm:px-7.5 xl:pb-1'>
                <div className='flex justify-between items-center mb-6'>
                    <p className='text-sm text-gray-600'>
                        {totalCount > 0 ? (
                            totalPages > 1 ?
                                `Showing ${((currentPage - 1) * 20) + 1}-${Math.min(currentPage * 20, totalCount)} of ${totalCount} results` :
                                `${totalCount} result${totalCount !== 1 ? 's' : ''} found`
                        ) : 'No results found'}
                    </p>
                    <div className='flex gap-3'>
                        <div className='relative' ref={columnSelectorRef}>
                            <button
                                onClick={() => setShowColumnSelector(!showColumnSelector)}
                                className='inline-flex items-center rounded-lg bg-gray-100 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-200 focus:outline-none focus:ring-4 focus:ring-gray-300 transition-colors duration-200'
                                title='Configure visible columns'
                            >
                                <FaCog className='mr-2' size={14} />
                                Columns
                            </button>
                            {showColumnSelector && (
                                <div className='absolute right-0 mt-2 w-64 bg-white rounded-lg shadow-lg border border-gray-200 z-50'>
                                    <div className='p-3 border-b border-gray-200'>
                                        <div className='flex justify-between items-center'>
                                            <span className='font-medium text-sm'>Visible Columns</span>
                                            <button
                                                onClick={handleResetColumns}
                                                className='text-xs text-primary hover:underline'
                                            >
                                                Reset to default
                                            </button>
                                        </div>
                                    </div>
                                    <div className='p-2 max-h-80 overflow-y-auto'>
                                        {ALL_AVAILABLE_COLUMNS.map(col => (
                                            <label
                                                key={col.key}
                                                className='flex items-center px-2 py-1.5 hover:bg-gray-50 rounded cursor-pointer'
                                            >
                                                <input
                                                    type='checkbox'
                                                    checked={visibleColumnKeys.includes(col.key)}
                                                    onChange={() => handleColumnToggle(col.key)}
                                                    className='w-4 h-4 text-primary border-gray-300 rounded focus:ring-primary'
                                                />
                                                <span className='ml-2 text-sm text-gray-700'>{col.label}</span>
                                            </label>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                        <button
                            onClick={handleDownloadCSV}
                            disabled={loading || isDownloading || totalCount === 0}
                            className='inline-flex items-center rounded-lg bg-green-100 px-4 py-2 text-sm font-medium text-green-700 hover:bg-green-200 focus:outline-none focus:ring-4 focus:ring-green-300 disabled:opacity-50 transition-colors duration-200'
                            title={totalCount > 20 ? `Download all ${totalCount} results as CSV` : 'Download results as CSV'}
                        >
                            {isDownloading ? (
                                <FaSync className='mr-2 animate-spin' size={14} />
                            ) : (
                                <FaDownload className='mr-2' size={14} />
                            )}
                            {isDownloading ? 'Downloading...' : `Download CSV${totalCount > 20 ? ` (${totalCount})` : ''}`}
                        </button>
                        <button
                            onClick={handleRefresh}
                            disabled={loading}
                            className='inline-flex items-center rounded-lg bg-gray-100 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-200 focus:outline-none focus:ring-4 focus:ring-gray-300 disabled:opacity-50 transition-colors duration-200'
                            title='Refresh results'
                        >
                            <FaSync className={`mr-2 ${loading ? 'animate-spin' : ''}`} size={14} />
                            Refresh
                        </button>
                        <button
                            onClick={handleBackToSearch}
                            className='rounded-lg bg-primary px-4 py-2 text-sm font-medium text-white hover:bg-primary/90 focus:outline-none focus:ring-4 focus:ring-primary/50 transition-colors duration-200'
                        >
                            Back to Search
                        </button>
                    </div>
                </div>

                {activeFilters.length > 0 && (
                    <div className='mb-6 flex flex-wrap items-center gap-2'>
                        <span className='text-sm font-medium text-gray-600'>Active filters:</span>
                        {activeFilters.map((filter, index) => (
                            <span
                                key={`${filter.key}-${filter.value}-${index}`}
                                className='inline-flex items-center gap-1.5 rounded-full bg-primary/10 px-3 py-1.5 text-sm font-medium text-primary'
                            >
                                <span className='text-gray-500'>{filter.label}:</span>
                                <span>{filter.value}</span>
                                <button
                                    onClick={() => handleRemoveFilter(filter.key, filter.rawValue)}
                                    className='ml-1 rounded-full p-0.5 hover:bg-primary/20 transition-colors'
                                    title={`Remove ${filter.label} filter`}
                                >
                                    <FaTimes size={10} />
                                </button>
                            </span>
                        ))}
                        <button
                            onClick={handleClearAllFilters}
                            className='ml-2 text-sm text-gray-500 hover:text-red-500 underline transition-colors'
                        >
                            Clear all
                        </button>
                    </div>
                )}

                {results.length === 0 && !loading ? (
                    <div className='text-center py-8'>
                        <div className='text-lg text-gray-600 mb-4'>No campaign applications found matching your criteria.</div>
                        <button
                            onClick={handleBackToSearch}
                            className='rounded-lg bg-primary px-6 py-2 text-white hover:bg-primary/90 font-medium'
                        >
                            Try Different Filters
                        </button>
                    </div>
                ) : results.length > 0 && (
                    <div className='max-w-full overflow-x-auto'>
                        <table className='w-full table-auto'>
                            <thead>
                                <tr className='bg-gray-2 text-left'>
                                    {columns.map((column, index) => (
                                        <th
                                            key={column.key}
                                            className={`min-w-[120px] py-4 px-4 font-medium text-black cursor-pointer hover:bg-gray-3 select-none ${index === 0 ? 'xl:pl-11' : ''}`}
                                            onClick={() => handleSort(column.sortKey)}
                                        >
                                            <div className='flex items-center'>
                                                {column.label}
                                                {getSortIcon(column.sortKey)}
                                            </div>
                                        </th>
                                    ))}
                                    <th className='py-4 px-4 font-medium text-black'>
                                        Actions
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                {results.map((app) => (
                                    <tr key={app.id}>
                                        {columns.map((column, index) => (
                                            <td
                                                key={column.key}
                                                className={`border-b border-[#eee] py-5 px-4 ${index === 0 ? 'pl-9 xl:pl-11' : ''}`}
                                            >
                                                {column.key === 'requirements' ? (
                                                    <span
                                                        className={`font-bold ${
                                                            (app.sections_completed?.completed || 0) === (app.sections_completed?.total || 6)
                                                                ? 'text-[#AFC12D]'
                                                                : 'text-[#F02D3A]'
                                                        }`}
                                                    >
                                                        {renderCellContent(app, column.key)}
                                                    </span>
                                                ) : column.key === 'patient_name' ? (
                                                    <button
                                                        onClick={() => navigate(`/campaign-applications/${app.id}`)}
                                                        className='font-bold text-primary hover:underline'
                                                    >
                                                        {renderCellContent(app, column.key)}
                                                    </button>
                                                ) : column.key === 'photo_url' ? (
                                                    app.campaign_photo_url ? (
                                                        <a
                                                            href={app.campaign_photo_url}
                                                            target='_blank'
                                                            rel='noopener noreferrer'
                                                            className='font-bold text-primary hover:underline'
                                                        >
                                                            View Photo
                                                        </a>
                                                    ) : (
                                                        <p className='text-black'>—</p>
                                                    )
                                                ) : (
                                                    <p className='text-black'>
                                                        {renderCellContent(app, column.key)}
                                                    </p>
                                                )}
                                            </td>
                                        ))}
                                        <td className='border-b border-[#eee] py-5 px-4'>
                                            <button
                                                onClick={() => navigate(`/campaign-applications/${app.id}`)}
                                                className='flex items-center justify-center w-8 h-8 rounded text-primary hover:text-primary/80 hover:bg-primary/10 transition-colors duration-200'
                                                title='View Campaign Application Details'
                                            >
                                                <FaEye size={16} />
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}

                {results.length > 0 && totalPages > 1 && (
                    <Pagination
                        page={currentPage}
                        setPage={handlePageChange}
                        pages={totalPages}
                        totalCount={totalCount}
                    />
                )}
            </div>
        </>
    );
};

export default CampaignApplicationSearchResults;
